import { useMemo, useState, useEffect } from "react";
import { Box, Stack, Typography, IconButton } from "@mui/material";
import { useDropzone } from "react-dropzone";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCloudArrowUp, faXmark, faFilePdf, faFileLines } from "@fortawesome/free-solid-svg-icons";
import { THEME_COLORS } from "../../../core/Constant";
import { getDropzoneAccept } from "../../../core/fileUtils";

const ThumbnailUploadComponent = ({
  value,
  onChange,
  accept = [".png", ".jpg", ".jpeg", ".webp", ".pdf"],
  size,
  maxSize,
  label = "Click to upload or drag and drop",
  description = "SVG, PNG, JPG",
  disabled = false,
  required = false,
  isError = false,
  error = "",
}) => {
  const [imgError, setImgError] = useState(false);

  useEffect(() => {
    setImgError(false);
  }, [value]);

  const dropzoneAccept = useMemo(() => getDropzoneAccept(accept), [accept]);

  const effectiveMaxSize = useMemo(() => {
    const s = maxSize ?? size;
    if (s === undefined || s === null) return null;
    if (typeof s === "number" && s > 0 && s <= 100) {
      return s * 1024 * 1024;
    }
    return typeof s === "number" ? s : null;
  }, [maxSize, size]);

  const { getRootProps, getInputProps, isDragActive, fileRejections, open } =
    useDropzone({
      noClick: true,
      disabled,
      multiple: false,
      accept: dropzoneAccept,
      maxSize: effectiveMaxSize,
      onDropAccepted: (acceptedFiles) => {
        if (acceptedFiles[0]) {
          onChange?.(acceptedFiles[0]);
        }
      },
      onDropRejected: () => {
        onChange?.(null);
      },
    });

  const fileErrorMessage = useMemo(() => {
    if (!fileRejections || fileRejections.length === 0) return null;
    const errorObj = fileRejections[0]?.errors?.[0];
    if (!errorObj) return null;

    if (errorObj.code === "file-invalid-type") {
      if (Array.isArray(accept) && accept.length > 0) {
        const types = accept
          .map((ext) =>
            typeof ext === "string" ? ext.replace(/^\./, "").toUpperCase() : ""
          )
          .filter(Boolean)
          .join(", ");
        return `File type must be one of ${types}`;
      }
      return "File type must be one of PDF, PNG, JPG, JPEG etc.";
    }

    if (errorObj.code === "file-too-large") {
      const maxMb = effectiveMaxSize
        ? (effectiveMaxSize / (1024 * 1024)).toFixed(0)
        : "";
      return maxMb
        ? `File should not be greater than ${maxMb} MB`
        : "File size is too large";
    }

    return errorObj.message;
  }, [fileRejections, accept, effectiveMaxSize]);

  const previewUrl = useMemo(() => {
    if (!value) return null;
    if (typeof value === "string") return value;
    if (value instanceof File || value instanceof Blob) {
      return URL.createObjectURL(value);
    }
    return null;
  }, [value]);

  // Check if file is a PDF
  const isPdf = useMemo(() => {
    if (!value) return false;
    if (value instanceof File || value instanceof Blob) {
      return value.type === "application/pdf" || value.name?.toLowerCase().endsWith(".pdf");
    }
    if (typeof value === "string") {
      return value.toLowerCase().split("?")[0].endsWith(".pdf");
    }
    return false;
  }, [value]);

  // Check if file extension is a valid image type
  const isImageFile = useMemo(() => {
    if (!value) return false;
    const imageExtensions = [".png", ".jpg", ".jpeg", ".gif", ".svg"];
    if (value instanceof File || value instanceof Blob) {
      return value.type.startsWith("image/") || imageExtensions.some((ext) => value.name?.toLowerCase().endsWith(ext));
    }
    if (typeof value === "string") {
      const cleanUrl = value.toLowerCase().split("?")[0];
      return imageExtensions.some((ext) => cleanUrl.endsWith(ext));
    }
    return false;
  }, [value]);

  const fileName = useMemo(() => {
    if (!value) return "";
    if (value.name) return value.name;
    if (typeof value === "string") {
      const parts = value.split("/");
      return parts[parts.length - 1];
    }
    return "Selected File";
  }, [value]);

  const fileSize = useMemo(() => {
    if (value && value.size) {
      const sizeInMB = value.size / (1024 * 1024);
      return `${sizeInMB.toFixed(2)} MB`;
    }
    return "";
  }, [value]);

  const handleRemove = (e) => {
    e.stopPropagation();
    onChange?.(null);
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Typography
        sx={{
          color: "#4E657C",
          fontSize: 18,
          fontWeight: 600,
          mb: 1.5,
        }}
      >
        {label}
        {required && (
          <Box component="span" sx={{ color: "#d32f2f", ml: 0.5 }}>
            *
          </Box>
        )}
      </Typography>

      <Box
        {...getRootProps()}
        onClick={disabled ? undefined : open}
        sx={{
          border: "2px dashed",
          borderColor: isDragActive ? THEME_COLORS.primary : "#D9DDF0",
          borderRadius: 3,
          p: 4,
          textAlign: "center",
          cursor: disabled ? "not-allowed" : "pointer",
          bgcolor: isDragActive ? "#F9F5FF" : "#FFFFFF",
          transition: "all .2s ease-in-out",
          opacity: disabled ? 0.6 : 1,
          "&:hover": {
            borderColor: disabled ? "#D0D5DD" : "#9834F0",
            bgcolor: disabled ? "#FFFFFF" : "#FAF5FF",
          },
        }}
      >
        <input {...getInputProps()} />

        <Box
          sx={{
            width: isDragActive ? 80 : 70,
            height: isDragActive ? 80 : 70,
            bgcolor: THEME_COLORS.primaryLight,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mx: "auto",
            mb: 2,
          }}
        >
          <FontAwesomeIcon
            icon={faCloudArrowUp}
            color={THEME_COLORS.primary}
            size={isDragActive ? "3x" : "2x"}
          />
        </Box>

        <Stack spacing={0.5}>
          <Typography fontWeight={700} fontSize={14} color="#101828">
            {label}
            {required && (
              <Box component="span" sx={{ color: "#d32f2f", ml: 0.5 }}>
                *
              </Box>
            )}
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
            fontSize={12}
          >
            {description}
          </Typography>
        </Stack>

        {fileErrorMessage && (
          <Typography mt={1.5} color="error" variant="body2" fontSize={12}>
            {fileErrorMessage}
          </Typography>
        )}
      </Box>

      {isError && error && (
        <Typography mt={0.5} color="error" variant="body2" fontSize={13}>
          {error}
        </Typography>
      )}

      {previewUrl && (
        <Box
          sx={{
            mt: 2,
            p: 1.5,
            border: "1px solid #EAECF0",
            borderRadius: "12px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            bgcolor: "#FFFFFF",
            boxShadow: "0px 1px 2px rgba(16, 24, 40, 0.05)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, overflow: "hidden" }}>
            {/* Conditional Rendering: PDF Icon -> Image -> File Fallback Icon */}
            {isPdf ? (
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: "8px",
                  bgcolor: "#FEF3F2",
                  border: "1px solid #FECDCA",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <FontAwesomeIcon icon={faFilePdf} color="#D92D20" size="xl" />
              </Box>
            ) : isImageFile && !imgError ? (
              <Box
                component="img"
                src={previewUrl}
                alt="Thumbnail Preview"
                onError={() => setImgError(true)}
                sx={{
                  width: 48,
                  height: 48,
                  objectFit: "cover",
                  borderRadius: "8px",
                  border: "1px solid #EAECF0",
                  flexShrink: 0,
                }}
              />
            ) : (
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: "8px",
                  bgcolor: "#F4EFFC",
                  border: "1px solid #E2D6F8",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <FontAwesomeIcon icon={faFileLines} color={THEME_COLORS.primary || "#9834F0"} size="xl" />
              </Box>
            )}

            <Box sx={{ minWidth: 0 }}>
              <Typography
                fontWeight={600}
                fontSize={14}
                color="#344054"
                noWrap
                title={fileName}
              >
                {fileName}
              </Typography>
              {fileSize && (
                <Typography fontSize={12} color="#667085">
                  {fileSize}
                </Typography>
              )}
            </Box>
          </Box>

          {!disabled && (
            <IconButton
              onClick={handleRemove}
              size="small"
              sx={{
                color: "#667085",
                "&:hover": {
                  color: "#D92D20",
                  bgcolor: "#FEF3F2",
                },
              }}
            >
              <FontAwesomeIcon icon={faXmark} />
            </IconButton>
          )}
        </Box>
      )}
    </Box>
  );
};

export default ThumbnailUploadComponent;